import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLACEMENTS = [
  { value: 'banner-top', label: 'Banner - Top' },
  { value: 'banner-middle', label: 'Banner - Middle' },
  { value: 'banner-bottom', label: 'Banner - Bottom' },
  { value: 'sidebar', label: 'Sidebar' }
];

function AdManager() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    link_url: '',
    placement: 'banner-top',
    active: true
  });

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API}/ads?active_only=false`);
      setAds(response.data);
    } catch (err) {
      console.error('Error fetching ads:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      if (editingAd) {
        await axios.put(`${API}/ads/${editingAd.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/ads`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      resetForm();
      fetchAds();
    } catch (err) {
      console.error('Error saving ad:', err);
      alert('Failed to save ad');
    }
  };

  const handleEdit = (ad) => {
    setEditingAd(ad);
    setFormData({
      title: ad.title,
      image_url: ad.image_url,
      link_url: ad.link_url,
      placement: ad.placement,
      active: ad.active
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this ad?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/ads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAds();
    } catch (err) {
      console.error('Error deleting ad:', err);
      alert('Failed to delete ad');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      image_url: '',
      link_url: '',
      placement: 'banner-top',
      active: true
    });
    setEditingAd(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-400">Loading ads...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-light">MANAGE ADS</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-6 py-2 bg-white text-black font-medium hover:bg-gray-200 transition-colors"
        >
          {showForm ? 'CANCEL' : 'NEW AD'}
        </button>
      </div>

      {showForm && (
        <div className="bg-zinc-900 border border-zinc-800 p-6 mb-8">
          <h3 className="text-xl font-light mb-6">{editingAd ? 'EDIT AD' : 'NEW AD'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Ad Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
                className="w-full bg-black border border-zinc-700 px-4 py-2 text-white focus:outline-none focus:border-white"
                placeholder="Kasango AI Promotion"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Image URL *</label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                required
                className="w-full bg-black border border-zinc-700 px-4 py-2 text-white focus:outline-none focus:border-white"
                placeholder="https://example.com/ad-image.jpg"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Link URL *</label>
              <input
                type="url"
                value={formData.link_url}
                onChange={(e) => setFormData({...formData, link_url: e.target.value})}
                required
                className="w-full bg-black border border-zinc-700 px-4 py-2 text-white focus:outline-none focus:border-white"
                placeholder="https://kasango.ai"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Placement *</label>
              <select
                value={formData.placement}
                onChange={(e) => setFormData({...formData, placement: e.target.value})}
                required
                className="w-full bg-black border border-zinc-700 px-4 py-2 text-white focus:outline-none focus:border-white"
              >
                {PLACEMENTS.map((placement) => (
                  <option key={placement.value} value={placement.value}>
                    {placement.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => setFormData({...formData, active: e.target.checked})}
                className="w-4 h-4"
              />
              <label htmlFor="active" className="text-sm text-gray-400">Active</label>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                className="px-6 py-2 bg-white text-black font-medium hover:bg-gray-200 transition-colors"
              >
                {editingAd ? 'UPDATE' : 'CREATE'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 border border-zinc-700 text-white hover:bg-zinc-800 transition-colors"
              >
                CANCEL
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {ads.length === 0 ? (
          <div className="col-span-2 text-center py-12 text-gray-500">No ads yet. Create your first ad!</div>
        ) : (
          ads.map((ad) => (
            <div key={ad.id} className="bg-zinc-900 border border-zinc-800 p-4">
              <div className="mb-4">
                <img
                  src={ad.image_url}
                  alt={ad.title}
                  className="w-full h-32 object-cover border border-zinc-700"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x150?text=Ad+Image';
                  }}
                />
              </div>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-medium">{ad.title}</h3>
                    {!ad.active && (
                      <span className="text-xs px-2 py-1 bg-red-900/30 text-red-400 border border-red-900">INACTIVE</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div><span className="text-gray-400">Placement:</span> {PLACEMENTS.find(p => p.value === ad.placement)?.label}</div>
                    <div className="truncate"><span className="text-gray-400">Link:</span> {ad.link_url}</div>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => handleEdit(ad)}
                  className="flex-1 px-4 py-2 border border-zinc-700 text-sm hover:bg-zinc-800 transition-colors"
                >
                  EDIT
                </button>
                <button
                  onClick={() => handleDelete(ad.id)}
                  className="flex-1 px-4 py-2 border border-red-900 text-red-400 text-sm hover:bg-red-900/20 transition-colors"
                >
                  DELETE
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AdManager;
